// OgrenciSMSIslemleri.tsx
import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useTheme,
    useMediaQuery,
    Typography,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Grid,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Divider,
    Tooltip,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Snackbar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import SendIcon from '@mui/icons-material/Send';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { OgrenciError } from '../../../types/ErrorModals/OgrenciError';
import { SMSError } from '../../../types/ErrorModals/SMSError';
import { getOgrenciler } from '../../../services/api/ogrenciService';
import { getSMSConfig, getSMSErrorMessage, isSMSErrorCode, sendSingleTextSMS } from '../../../services/api/smsService';
import { OgrenciResponseModal, SMSConfigModal } from '../../../types/modals';
import { useInstitution } from '../../../contexts/InstitutionContext';
import { getCurrentSezon, getSezonOptions } from '../../../utils/sezon';

interface TelefonSecimi {
    ogrenciTel: boolean;
    anneTel: boolean;
    babaTel: boolean;
    veliTel: boolean;
}

interface OgrenciSecimi {
    ogrenci: OgrenciResponseModal;
    secimler: TelefonSecimi;
}

const OgrenciSMSIslemleri: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { kullaniciModal } = useAuth();
    const { selectedSubInstitution, institution } = useInstitution();

    // Get the appropriate institution code based on selection
    const kurumKodu = useMemo(() => {
        // If a sub-institution is selected, use its code
        if (selectedSubInstitution) {
            return selectedSubInstitution.code;
        }
        // Otherwise use the main institution code
        if (institution.code)
            return institution.code;

        return localStorage.getItem('kurumKodu') || '';
    }, [selectedSubInstitution, institution]);

    // SMS konfigürasyon bilgilerini almak için state ve query ekleyelim
    const [smsConfig, setSmsConfig] = useState<SMSConfigModal | null>(null);
    const [loadingConfig, setLoadingConfig] = useState<boolean>(false);

    // Sezon state'i (sezon = eğitim yılının BİTİŞ yılı)
    const [selectedSezon, setSelectedSezon] = useState<number>(getCurrentSezon());

    // Filtre state'leri
    const [searchText, setSearchText] = useState<string>('');
    const [filterField, setFilterField] = useState<string>('adiSoyadi');
    const [filteredData, setFilteredData] = useState<OgrenciResponseModal[]>([]);
    const [sinifFilter, setSinifFilter] = useState<string>('');
    const [siniflar, setSiniflar] = useState<string[]>([]);

    // Seçim state'leri
    const [secilenOgrenciler, setSecilenOgrenciler] = useState<OgrenciSecimi[]>([]);
    const [tumOgrenciTelSecili, setTumOgrenciTelSecili] = useState<boolean>(false);
    const [tumAnneTelSecili, setTumAnneTelSecili] = useState<boolean>(false);
    const [tumBabaTelSecili, setTumBabaTelSecili] = useState<boolean>(false);
    const [tumVeliTelSecili, setTumVeliTelSecili] = useState<boolean>(false);

    // SMS dialog state'leri
    const [smsDialogOpen, setSmsDialogOpen] = useState<boolean>(false);
    const [smsMessage, setSmsMessage] = useState<string>('');
    const [sendingSMS, setSendingSMS] = useState<boolean>(false);

    // Snackbar state'leri
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

    // Sezon değişikliğini yönetmek için handler
    const handleSezonChange = (event: SelectChangeEvent<number>) => {
        setSelectedSezon(event.target.value as number);
    };

    // Arama metni değişikliğini yönetmek için handler
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
    };

    // Filtre alanı değişikliğini yönetmek için handler
    const handleFilterFieldChange = (event: SelectChangeEvent<string>) => {
        setFilterField(event.target.value);
    };

    // Sınıf filtresi değişikliğini yönetmek için handler
    const handleSinifFilterChange = (event: SelectChangeEvent<string>) => {
        setSinifFilter(event.target.value);
    };

    // Arama metnini temizlemek için handler
    const handleClearSearch = () => {
        setSearchText('');
    };

    // Sezon seçenekleri: geçmiş 2 + mevcut + gelecek 1 yıl (değer = eğitim yılının bitiş yılı)
    const sezonOptions = getSezonOptions();

    // Öğrencileri getirmek için query
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['ogrenciler', kurumKodu, kullaniciModal?.id, selectedSezon],
        queryFn: async () => {
            try {
                return await getOgrenciler({
                    kurumKodu: kurumKodu,
                    sezon: selectedSezon
                });
            } catch (err) {
                const ogrenciError = err as OgrenciError;
                throw new Error(ogrenciError.message);
            }
        },
        enabled: !!kurumKodu,
        retry: 1
    });

    // Sınıf listesini oluştur
    useEffect(() => {
        if (data) {
            const uniqueSiniflar = Array.from(new Set(data.map(o => o.sinif))).filter(Boolean);
            setSiniflar(uniqueSiniflar.sort());
        }
    }, [data]);

    // Veri filtreleme
    useEffect(() => {
        if (!data) return;

        let filtered = [...data];

        // Sınıf filtresi uygula
        if (sinifFilter) {
            filtered = filtered.filter(ogrenci => ogrenci.sinif === sinifFilter);
        }

        // Arama filtresi uygula
        if (searchText.trim()) {
            const lowercaseSearchText = searchText.toLowerCase().trim();

            filtered = filtered.filter(ogrenci => {
                switch (filterField) {
                    case 'adiSoyadi':
                        return ogrenci.adiSoyadi.toLowerCase().includes(lowercaseSearchText);
                    case 'no':
                        return ogrenci.no.toLowerCase().includes(lowercaseSearchText);
                    case 'sinif':
                        return ogrenci.sinif.toLowerCase().includes(lowercaseSearchText);
                    default:
                        return ogrenci.adiSoyadi.toLowerCase().includes(lowercaseSearchText);
                }
            });
        }

        setFilteredData(filtered);
    }, [data, searchText, filterField, sinifFilter]);

    // Öğrenci seçimi değiştiğinde seçim state'ini güncelle
    const handleOgrenciSecimi = (ogrenci: OgrenciResponseModal, telefonTipi: keyof TelefonSecimi, secildi: boolean) => {
        const mevcutIndex = secilenOgrenciler.findIndex(item => item.ogrenci.recNo === ogrenci.recNo);

        if (mevcutIndex === -1 && secildi) {
            // Öğrenci daha önce seçilmemişse ve şimdi seçildiyse, ekle
            const yeniSecim: OgrenciSecimi = {
                ogrenci,
                secimler: {
                    ogrenciTel: telefonTipi === 'ogrenciTel',
                    anneTel: telefonTipi === 'anneTel',
                    babaTel: telefonTipi === 'babaTel',
                    veliTel: telefonTipi === 'veliTel'
                }
            };
            setSecilenOgrenciler([...secilenOgrenciler, yeniSecim]);
        } else if (mevcutIndex !== -1) {
            // Öğrenci daha önce seçilmişse, güncelle
            const yeniSecilenler = [...secilenOgrenciler];
            yeniSecilenler[mevcutIndex].secimler[telefonTipi] = secildi;

            // Eğer hiçbir telefon seçili değilse öğrenciyi listeden çıkar
            if (!Object.values(yeniSecilenler[mevcutIndex].secimler).some(val => val)) {
                yeniSecilenler.splice(mevcutIndex, 1);
            }

            setSecilenOgrenciler(yeniSecilenler);
        }
    };

    // Tüm öğrencilerin belirli bir telefon tipini seçme/kaldırma
    const handleTumTelefonSecimi = (telefonTipi: keyof TelefonSecimi, secildi: boolean) => {
        // İlgili state'i güncelle
        switch (telefonTipi) {
            case 'ogrenciTel':
                setTumOgrenciTelSecili(secildi);
                break;
            case 'anneTel':
                setTumAnneTelSecili(secildi);
                break;
            case 'babaTel':
                setTumBabaTelSecili(secildi);
                break;
            case 'veliTel':
                setTumVeliTelSecili(secildi);
                break;
        }

        // Filtrelenmiş verilerdeki tüm öğrenciler için seçimi uygula
        if (secildi) {
            // Seçilecek öğrencileri belirle
            const yeniSecilenler: OgrenciSecimi[] = [];

            filteredData.forEach(ogrenci => {
                const mevcutIndex = secilenOgrenciler.findIndex(item => item.ogrenci.recNo === ogrenci.recNo);

                if (mevcutIndex === -1) {
                    // Öğrenci daha önce seçilmemişse
                    const yeniSecim: OgrenciSecimi = {
                        ogrenci,
                        secimler: {
                            ogrenciTel: telefonTipi === 'ogrenciTel',
                            anneTel: telefonTipi === 'anneTel',
                            babaTel: telefonTipi === 'babaTel',
                            veliTel: telefonTipi === 'veliTel'
                        }
                    };
                    yeniSecilenler.push(yeniSecim);
                } else {
                    // Öğrenci daha önce seçilmişse
                    const guncelSecim = { ...secilenOgrenciler[mevcutIndex] };
                    guncelSecim.secimler[telefonTipi] = true;
                    yeniSecilenler.push(guncelSecim);
                }
            });

            // Filtrelenmiş verilerde olmayan önceden seçilmiş öğrencileri de ekle
            secilenOgrenciler.forEach(secim => {
                if (!filteredData.some(o => o.recNo === secim.ogrenci.recNo)) {
                    yeniSecilenler.push(secim);
                }
            });

            setSecilenOgrenciler(yeniSecilenler);
        } else {
            // Seçimi kaldır - sadece filtrelenmiş verilerdeki öğrenciler için
            const yeniSecilenler = secilenOgrenciler.map(secim => {
                // Eğer öğrenci filtrelenmiş verilerde varsa, seçimi kaldır
                if (filteredData.some(o => o.recNo === secim.ogrenci.recNo)) {
                    return {
                        ...secim,
                        secimler: {
                            ...secim.secimler,
                            [telefonTipi]: false
                        }
                    };
                }
                // Filtrelenmiş verilerde yoksa, değiştirme
                return secim;
            });

            // Hiçbir telefon seçili olmayan öğrencileri listeden çıkar
            const filtrelenmisSecilenler = yeniSecilenler.filter(secim =>
                Object.values(secim.secimler).some(val => val)
            );

            setSecilenOgrenciler(filtrelenmisSecilenler);
        }
    };

    // SMS gönderme dialogunu açma
    const handleSmsDialogOpen = () => {
        if (secilenOgrenciler.length === 0) {
            setSnackbarMessage('Lütfen en az bir öğrenci ve telefon numarası seçin');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        setSmsDialogOpen(true);
    };

    // SMS gönderme dialogunu kapatma
    const handleSmsDialogClose = () => {
        setSmsDialogOpen(false);
        setSmsMessage('');
    };

    // SMS mesajı değişikliğini yönetmek için handler
    const handleSmsMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSmsMessage(event.target.value);
    };

    // Telefon numarasını formatlama fonksiyonu
    const formatTelefon = (telefon: string | undefined): string => {
        if (!telefon) return "-";

        // Boşlukları ve özel karakterleri temizle
        const temizNumara = telefon.replace(/\s+/g, '').replace(/[^\d]/g, '');

        // Numarayı kısalt ve format ekle
        if (temizNumara.length >= 10) {
            return `${temizNumara.substring(0, 3)}...${temizNumara.substring(temizNumara.length - 2)}`;
        }

        return temizNumara;
    };

    // SMS konfigürasyonunu yükleme fonksiyonu
    const loadSMSConfig = async () => { 
        if (!kurumKodu) return;

        setLoadingConfig(true);
        try {
            const config = await getSMSConfig(kurumKodu);
            setSmsConfig(config);
        } catch (error) {
            const smsError = error as SMSError;
            setSnackbarMessage(`SMS ayarları alınamadı: ${smsError.message}`);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setLoadingConfig(false);
        }
    };

    useEffect(() => {
        loadSMSConfig();
    }, []);

    // SMS gönderme işlemi
    const handleSendSms = async () => {
        if (!smsMessage.trim()) {
            setSnackbarMessage('Lütfen bir mesaj girin');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        // SMS konfigürasyonu yüklenmemişse
        if (!smsConfig) {
            setSnackbarMessage('SMS ayarları yüklenemedi, lütfen sayfayı yenileyin');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        // Seçilen tüm telefon numaralarını topla
        const telefonNumaralari: string[] = [];

        secilenOgrenciler.forEach(secim => {
            const { ogrenci, secimler } = secim;

            if (secimler.ogrenciTel) {
                if (ogrenci.ogrenciTel1) telefonNumaralari.push(ogrenci.ogrenciTel1);
                if (ogrenci.ogrenciTel2) telefonNumaralari.push(ogrenci.ogrenciTel2);
            }

            if (secimler.anneTel) {
                if (ogrenci.anneTel1) telefonNumaralari.push(ogrenci.anneTel1);
                if (ogrenci.anneTel2) telefonNumaralari.push(ogrenci.anneTel2);
            }

            if (secimler.babaTel) {
                if (ogrenci.babaTel1) telefonNumaralari.push(ogrenci.babaTel1);
                if (ogrenci.babaTel2) telefonNumaralari.push(ogrenci.babaTel2);
            }

            if (secimler.veliTel) {
                if (ogrenci.akrabaTel1) telefonNumaralari.push(ogrenci.akrabaTel1);
                if (ogrenci.akrabaTel2) telefonNumaralari.push(ogrenci.akrabaTel2);
            }
        });

        // Boş numaraları filtrele, benzersiz hale getir ve sadece rakamları al
        const filtrelenmisNumaralar = Array.from(
            new Set(
                telefonNumaralari
                    .filter(num => num && num.trim() !== '')
                    .map(num => num.replace(/\D/g, '')) // Sadece rakamları al
            )
        );

        if (filtrelenmisNumaralar.length === 0) {
            setSnackbarMessage('Seçilen öğrencilerin geçerli telefon numarası bulunamadı');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        // Telefon numaralarının uzunluğunu kontrol et (10-13 karakter)
        const gecersizNumaralar = filtrelenmisNumaralar.filter(num => num.length < 10 || num.length > 13);
        if (gecersizNumaralar.length > 0) {
            setSnackbarMessage(`${gecersizNumaralar.length} adet geçersiz telefon numarası bulundu. Numaralar 10-13 karakter arasında olmalıdır.`);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        setSendingSMS(true);

        try {
            const result = await sendSingleTextSMS({
                kurumKodu: kurumKodu,
                message: smsMessage,
                numbers: filtrelenmisNumaralar.join(','),
                originator: smsConfig?.originator || '',
                scheduleDate: '',
                expiryDate: ''
            });

            // Başarılı yanıt kontrolü
            if (isSMSErrorCode(result.messageId)) {
                // Hata kodu döndüyse
                setSnackbarMessage(`SMS gönderimi başarısız: ${getSMSErrorMessage(result.messageId)}`);
                setSnackbarSeverity('error');
            } else {
                // Başarılı ise
                setSnackbarMessage(`${filtrelenmisNumaralar.length} numaraya SMS başarıyla gönderildi`);
                setSnackbarSeverity('success');
                handleSmsDialogClose();

                // Seçimleri temizle
                setSecilenOgrenciler([]);
                setTumOgrenciTelSecili(false);
                setTumAnneTelSecili(false);
                setTumBabaTelSecili(false);
                setTumVeliTelSecili(false);
            }
        } catch (error) {
            const smsError = error as SMSError;

            let errorMessage = `SMS gönderilemedi: ${smsError.message}`;

            // API'den gelen hata alanlarını kontrol et
            if (smsError.errors && typeof smsError.errors === 'object') {
                const errorDetails = Object.entries(smsError.errors)
                    .map(([field, messages]) => {
                        if (Array.isArray(messages)) {
                            return `${field}: ${messages.join(', ')}`;
                        } else if (typeof messages === 'string') {
                            return `${field}: ${messages}`;
                        } else {
                            return `${field}: Bilinmeyen hata`;
                        }
                    })
                    .join('\n');

                if (errorDetails) {
                    errorMessage = `SMS gönderilemedi:\n${errorDetails}`;
                }
            }

            setSnackbarMessage(errorMessage);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setSendingSMS(false);
            setSnackbarOpen(true);
        }
    };
    // Snackbar kapatma
    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    // Öğrencinin belirli bir telefon tipinin seçili olup olmadığını kontrol et
    const isTelefonSecili = (ogrenci: OgrenciResponseModal, telefonTipi: keyof TelefonSecimi): boolean => {
        const secim = secilenOgrenciler.find(item => item.ogrenci.recNo === ogrenci.recNo);
        return secim ? secim.secimler[telefonTipi] : false;
    };

    // Öğrencinin belirli bir telefon tipinin numarası var mı kontrol et
    const hasTelefon = (ogrenci: OgrenciResponseModal, telefonTipi: keyof TelefonSecimi): boolean => {
        switch (telefonTipi) {
            case 'ogrenciTel':
                return !!(ogrenci.ogrenciTel1?.trim() || ogrenci.ogrenciTel2?.trim());
            case 'anneTel':
                return !!(ogrenci.anneTel1?.trim() || ogrenci.anneTel2?.trim());
            case 'babaTel':
                return !!(ogrenci.babaTel1?.trim() || ogrenci.babaTel2?.trim());
            case 'veliTel':
                return !!(ogrenci.akrabaTel1?.trim() || ogrenci.akrabaTel2?.trim());
            default:
                return false;
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert
                severity="error"
                sx={{ mt: 2 }}
                action={
                    process.env.NODE_ENV === 'development' && (
                        <Typography variant="caption" display="block">
                            CorrelationId: {(error as OgrenciError).correlationId}
                        </Typography>
                    )
                }
            >
                {(error as Error).message}
            </Alert>
        );
    }

    return (
        <Box sx={{ mt: { xs: 2, sm: 3 } }}>
            {/* Sezon ve sınıf seçimi */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="sezon-select-label">Sezon</InputLabel>
                            <Select
                                labelId="sezon-select-label"
                                id="sezon-select"
                                value={selectedSezon}
                                label="Sezon"
                                onChange={handleSezonChange}
                            >
                                {sezonOptions.map((year) => (
                                    <MenuItem key={year} value={year}>
                                        {year - 1} - {year} Eğitim Yılı
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="sinif-filter-label">Sınıf Filtresi</InputLabel>
                            <Select
                                labelId="sinif-filter-label"
                                id="sinif-filter-select"
                                value={sinifFilter}
                                label="Sınıf Filtresi"
                                onChange={handleSinifFilterChange}
                                displayEmpty
                            >
                                {siniflar.map((sinif) => (
                                    <MenuItem key={sinif} value={sinif}>
                                        {sinif}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => refetch()}
                            startIcon={<FilterListIcon />}
                        >
                            Listele
                        </Button>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="filter-field-label">Filtre Alanı</InputLabel>
                            <Select
                                labelId="filter-field-label"
                                id="filter-field-select"
                                value={filterField}
                                label="Filtre Alanı"
                                onChange={handleFilterFieldChange}
                            >
                                <MenuItem value="adiSoyadi">Adı Soyadı</MenuItem>
                                <MenuItem value="no">Öğrenci No</MenuItem>
                                <MenuItem value="sinif">Sınıf</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Ara"
                            variant="outlined"
                            value={searchText}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: searchText && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="clear search"
                                            onClick={handleClearSearch}
                                            edge="end"
                                            size="small"
                                        >
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            placeholder={`${filterField === 'adiSoyadi' ? 'Öğrenci adı' : filterField === 'no' ? 'Öğrenci no' : 'Sınıf'} ile ara...`}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            startIcon={<SendIcon />}
                            onClick={handleSmsDialogOpen}
                            disabled={secilenOgrenciler.length === 0}
                        >
                            SMS Gönder ({secilenOgrenciler.length})
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Öğrenci tablosu */}
            {!data?.length ? (
                <Alert severity="info">
                    Seçilen sezon için kayıtlı öğrenci bulunmamaktadır.
                </Alert>
            ) : filteredData.length === 0 ? (
                <Alert severity="info">
                    Arama kriterlerine uygun öğrenci bulunamadı.
                </Alert>
            ) : (
                <Box sx={{ overflowX: "auto" }}>
                    <TableContainer component={Paper} sx={{ minWidth: isMobile ? 600 : 800 }}>
                        <Table aria-label="öğrenci listesi tablosu">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                                        Ad Soyad
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={tumOgrenciTelSecili}
                                                    onChange={(e) => handleTumTelefonSecimi('ogrenciTel', e.target.checked)}
                                                    color="primary"
                                                    size="small"
                                                />
                                            }
                                            label="Öğrenci Tel"
                                            sx={{ margin: 0, '& .MuiFormControlLabel-label': { color: 'white', fontSize: '0.875rem' } }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={tumAnneTelSecili}
                                                    onChange={(e) => handleTumTelefonSecimi('anneTel', e.target.checked)}
                                                    color="primary"
                                                    size="small"
                                                />
                                            }
                                            label="Anne Tel"
                                            sx={{ margin: 0, '& .MuiFormControlLabel-label': { color: 'white', fontSize: '0.875rem' } }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={tumBabaTelSecili}
                                                    onChange={(e) => handleTumTelefonSecimi('babaTel', e.target.checked)}
                                                    color="primary"
                                                    size="small"
                                                />
                                            }
                                            label="Baba Tel"
                                            sx={{ margin: 0, '& .MuiFormControlLabel-label': { color: 'white', fontSize: '0.875rem' } }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={tumVeliTelSecili}
                                                    onChange={(e) => handleTumTelefonSecimi('veliTel', e.target.checked)}
                                                    color="primary"
                                                    size="small"
                                                />
                                            }
                                            label="Veli Tel"
                                            sx={{ margin: 0, '& .MuiFormControlLabel-label': { color: 'white', fontSize: '0.875rem' } }}
                                        />
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.map((ogrenci, index) => (
                                    <TableRow
                                        key={ogrenci.recNo || index}
                                        sx={{
                                            "&:nth-of-type(odd)": { backgroundColor: "#f9f9f9" },
                                            "&:hover": {
                                                backgroundColor: "rgba(232, 137, 16, 0.1)",
                                                transition: "background-color 0.3s ease",
                                            },
                                        }}
                                    >
                                        <TableCell>{ogrenci.adiSoyadi}</TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center">
                                                <Checkbox
                                                    checked={isTelefonSecili(ogrenci, 'ogrenciTel')}
                                                    onChange={(e) => handleOgrenciSecimi(ogrenci, 'ogrenciTel', e.target.checked)}
                                                    color="primary"
                                                    disabled={!hasTelefon(ogrenci, 'ogrenciTel')}
                                                />
                                                {hasTelefon(ogrenci, 'ogrenciTel') ? (
                                                    <Tooltip title={`${ogrenci.ogrenciTel1 || ''} ${ogrenci.ogrenciTel2 ? '/ ' + ogrenci.ogrenciTel2 : ''}`}>
                                                        <Typography variant="body2" component="span">
                                                            {formatTelefon(ogrenci.ogrenciTel1 || ogrenci.ogrenciTel2)}
                                                        </Typography>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography variant="body2" component="span" color="text.secondary">
                                                        -
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center">
                                                <Checkbox
                                                    checked={isTelefonSecili(ogrenci, 'anneTel')}
                                                    onChange={(e) => handleOgrenciSecimi(ogrenci, 'anneTel', e.target.checked)}
                                                    color="primary"
                                                    disabled={!hasTelefon(ogrenci, 'anneTel')}
                                                />
                                                {hasTelefon(ogrenci, 'anneTel') ? (
                                                    <Tooltip title={`${ogrenci.anneTel1 || ''} ${ogrenci.anneTel2 ? '/ ' + ogrenci.anneTel2 : ''}`}>
                                                        <Typography variant="body2" component="span">
                                                            {formatTelefon(ogrenci.anneTel1 || ogrenci.anneTel2)}
                                                        </Typography>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography variant="body2" component="span" color="text.secondary">
                                                        -
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center">
                                                <Checkbox
                                                    checked={isTelefonSecili(ogrenci, 'babaTel')}
                                                    onChange={(e) => handleOgrenciSecimi(ogrenci, 'babaTel', e.target.checked)}
                                                    color="primary"
                                                    disabled={!hasTelefon(ogrenci, 'babaTel')}
                                                />
                                                {hasTelefon(ogrenci, 'babaTel') ? (
                                                    <Tooltip title={`${ogrenci.babaTel1 || ''} ${ogrenci.babaTel2 ? '/ ' + ogrenci.babaTel2 : ''}`}>
                                                        <Typography variant="body2" component="span">
                                                            {formatTelefon(ogrenci.babaTel1 || ogrenci.babaTel2)}
                                                        </Typography>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography variant="body2" component="span" color="text.secondary">
                                                        -
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center">
                                                <Checkbox
                                                    checked={isTelefonSecili(ogrenci, 'veliTel')}
                                                    onChange={(e) => handleOgrenciSecimi(ogrenci, 'veliTel', e.target.checked)}
                                                    color="primary"
                                                    disabled={!hasTelefon(ogrenci, 'veliTel')}
                                                />
                                                {hasTelefon(ogrenci, 'veliTel') ? (
                                                    <Tooltip title={`${ogrenci.akrabaTel1 || ''} ${ogrenci.akrabaTel2 ? '/ ' + ogrenci.akrabaTel2 : ''}`}>
                                                        <Typography variant="body2" component="span">
                                                            {formatTelefon(ogrenci.akrabaTel1 || ogrenci.akrabaTel2)}
                                                        </Typography>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography variant="body2" component="span" color="text.secondary">
                                                        -
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Kayıt sayısı bilgisi */}
                    <Typography variant="body2" sx={{ mt: 1, textAlign: 'right', color: 'text.secondary' }}>
                        Toplam {filteredData.length} öğrenci listeleniyor
                        {searchText && ` (Filtre: ${searchText})`}
                        {sinifFilter && ` (Sınıf: ${sinifFilter})`}
                    </Typography>

                    {/* Seçili öğrenci sayısı bilgisi */}
                    <Typography variant="body2" sx={{ mt: 0.5, textAlign: 'right', color: 'primary.main', fontWeight: 'bold' }}>
                        {secilenOgrenciler.length > 0 ? `${secilenOgrenciler.length} öğrenci seçildi` : ''}
                    </Typography>
                </Box>
            )}

            {/* SMS Gönderme Dialog */}
            <Dialog
                open={smsDialogOpen}
                onClose={handleSmsDialogClose}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>SMS Gönder</DialogTitle>
                <DialogContent>
                    {loadingConfig ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box sx={{ mt: 1 }}>
                            <TextField
                                autoFocus
                                margin="dense"
                                id="sms-message"
                                label="SMS Metni"
                                type="text"
                                fullWidth
                                multiline
                                rows={4}
                                value={smsMessage}
                                onChange={handleSmsMessageChange}
                                inputProps={{ maxLength: 160 }}
                                helperText={`${smsMessage.length}/160 karakter`}
                            />

                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    {secilenOgrenciler.length} öğrenciye ait telefon numaralarına SMS gönderilecektir.
                                </Typography>
                                {smsConfig && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Gönderen Adı: <strong>{smsConfig.originator}</strong>
                                    </Typography>
                                )}
                            </Alert>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSmsDialogClose} color="primary">
                        İptal
                    </Button>
                    <Button
                        onClick={handleSendSms}
                        color="primary"
                        variant="contained"
                        disabled={!smsMessage.trim() || sendingSMS || loadingConfig || !smsConfig}
                        startIcon={sendingSMS ? <CircularProgress size={20} /> : <SendIcon />}
                    >
                        {sendingSMS ? 'Gönderiliyor...' : 'Gönder'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bildirim Snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default OgrenciSMSIslemleri;